import React, { forwardRef, useRef, useState, useMemo, useCallback, useEffect, useImperativeHandle } from "react";
import { Card } from "antd";
import { getAllNotes } from "@/services/notes";
import CreateNote from "@/components/Notes/components/CreateNote";
import { DisplayNotesType, NotesListPaginationType } from "@/components/Notes/constants";
import CustomNotesList from "./components/CustomNotesList";

const Notes = forwardRef(function Notes(
  {
    type,
    title,
    entityId,
    display = DisplayNotesType.Card,
    paginationType = NotesListPaginationType.Normal,
    onNotesCountChange,
  },
  ref,
) {
  const fetchedOnce = useRef(false);

  const [loadingNotes, setLoadingNotes] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [notes, setNotes] = useState([]);

  const getNotes = useCallback(
    async (_page, _pageSize) => {
      setLoadingNotes(true);

      const localPage = typeof _page === "number" ? _page : page;
      const localPageSize = typeof _pageSize === "number" ? _pageSize : pageSize;

      setPage(localPage);
      setPageSize(localPageSize);

      getAllNotes({
        id: entityId,
        // if receive _page/_pageSize get that page, otherwise stay at the same
        // page (eg: reloading because an edit).
        page: localPage,
        pageSize: localPageSize,
      })
        .then(data => {
          setNotes(data?.items || []);
          setTotalItems(data?.totalCount || 0);
        })
        .finally(() => {
          setLoadingNotes(false);
        });
    },
    [entityId, page, pageSize],
  );

  useImperativeHandle(
    ref,
    () => {
      return {
        refresh() {
          const newPage = 1;
          const newPageSize = 10;
          getNotes(newPage, newPageSize);
        },
      };
    },
    [getNotes],
  );

  useEffect(() => {
    if (fetchedOnce.current) {
      return;
    }

    fetchedOnce.current = true;
    getNotes();
  }, [getNotes]);

  const onPageChange = useCallback(
    newPage => {
      getNotes(newPage, pageSize);
    },
    [pageSize, getNotes],
  );

  const onPageSizeChange = useCallback(
    newPageSize => {
      getNotes(page, newPageSize);
    },
    [page, getNotes],
  );

  const notesList = useMemo(() => {
    return (
      <CustomNotesList
        notes={notes}
        totalItems={totalItems}
        getNotes={getNotes}
        loading={loadingNotes}
        page={page}
        pageSize={pageSize}
        onNotesCountChange={onNotesCountChange}
        onlyList={display === DisplayNotesType.OnlyList}
      />
    );
  }, [
    paginationType,
    notes,
    page,
    pageSize,
    totalItems,
    loadingNotes,
    getNotes,
    onNotesCountChange,
    onPageChange,
    onPageSizeChange,
    display,
  ]);

  if (display === DisplayNotesType.OnlyList) {
    return notesList;
  }

  return (
    <Card
      bordered={false}
      title={title || "Notes"}
      headStyle={{ borderBottom: "unset" }}
      extra={[
        <CreateNote
          key="add-note"
          type={type}
          entityId={entityId}
          getNotes={getNotes}
          onNotesCountChange={onNotesCountChange}
        />,
      ]}
    >
      {notesList}
    </Card>
  );
});

export default Notes;
